import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import { ApplicationSdk } from "../..";

interface Object_Simple {
    id: number;
    name: string;
}


/**
 * The dialog store for the manager
 */
class ObjectDialogStore {
    private sdk: ApplicationSdk;

    public activeTab: number = 0;
    public state: string = 'add';

    public object_id: number | null = null;

    public object_number: number | null = null;
    public object_name: string = '';
    public object_street: string = '';
    public object_housenumber: string = '';
    public object_zipcode: string = '';
    public object_city: string = '';
    public object_note: string = '';
    public object_archived: boolean = false;
    public object_representation: boolean = false;

    public object_assigned_cleaner: Set<number> = new Set();
    public object_assigned_products: Set<number> = new Set();

    public selected_manager_id: number | null = null; // Selected manager ID
    public available_managers: Array<{ id: number; name: string }> = []; // List of available managers

    public objectDataLoading: boolean = false;

    public cleanerDataFetched: boolean = false;
    public cleanerDataLoading: boolean = false;
    public assignedCleaner: Set<number> = new Set();
    public unassignedCleaner: Set<number> = new Set();
    public availableCleaner: Map<number, string> = new Map();

    public productDataFetched: boolean = false;
    public productDataLoading: boolean = false;
    public assignedProducts: Set<number> = new Set();
    public unassignedProducts: Set<number> = new Set();
    public availableProducts: Map<number, any> = new Map();

    public showArchiveConfirmation: boolean = false;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            activeTab: observable,
            object_id: observable,
            object_number: observable,
            object_name: observable,
            state: observable,
            object_street: observable,
            showArchiveConfirmation: observable,
            object_housenumber: observable,
            object_zipcode: observable,
            object_city: observable,
            object_note: observable,
            selected_manager_id: observable,
            available_managers: observable,
            object_assigned_cleaner: observable,
            object_assigned_products: observable,
            cleanerDataLoading: observable,
            assignedCleaner: observable,
            unassignedCleaner: observable,
            availableCleaner: observable,
            objectDataLoading: observable,
            productDataLoading: observable,
            assignedProducts: observable,
            unassignedProducts: observable,
            availableProducts: observable,
            object_archived: observable,
            object_representation: observable,
            onChangeTab: action,
            selectManager: action,
            archiveObject: action,
            fetchCleaner: action,
            fetchObject: action,
            clone: action,
            setShowArchiveConfirmation: action,
            fetchAvailableManagers: action,
            updateCleanerAssignment: action,
            updateProductsAssignment: action,
            resetProductsAssignment: action,
            onSaveObject: action,
            objectname: computed,
            objectnote: computed,
            street: computed,
            housenumber: computed,
            zipcode: computed,
            city: computed,
            assignedCleanerArray: computed,
            unassignedCleanerArray: computed,
            assignedProductsArray: computed,
            unassignedProductsArray: computed,
            archived: computed,
            id: computed,
            isCloneable: computed,
            isArchivable: computed,
            isRepresentation: computed,
            isLoading: computed,
            validObjectname: computed,
            validObjectId: computed,
            isInvalid: computed
        });

        this.sdk = sdk;
    }

    // Clone the current product
    public clone(): void {
        this.object_id = null;
        this.state = 'add';
        this.object_name = `${this.object_name} (Kopie)`;
    }


    public setShowArchiveConfirmation(show: boolean): void {
        this.showArchiveConfirmation = show;
    }

    public async init(config: any): Promise<void> {
        if (config?.object_id) {
            this.object_id = config.object_id;
            this.state = 'update';
        }
        await this.fetchObject(config?.object_id || null);
        await this.fetchCleaner();
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_managers.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: `${manager.firstname} ${manager.lastname}`
                }));
            }
        });
    }

    public async fetchCleaner(): Promise<void> {
        if (this.cleanerDataFetched) {
            return;
        }
        // Reset
        runInAction(() => {
            this.cleanerDataLoading = true;
            this.assignedCleaner = new Set();
            this.unassignedCleaner = new Set();
        })

        const cleanerList: any = await this.sdk.fetchApi(
            'cleaner',
            'query',
            {
                format: 'simple',
                limit: 999
            }
        );

        runInAction(() => {
            this.cleanerDataLoading = false;

            if (cleanerList?.status === 200) {
                this.cleanerDataFetched = true;
                (cleanerList.json.data as Array<any>).forEach((cleaner: any) => {
                    if (!this.availableCleaner.has(cleaner.id)) {
                        this.availableCleaner.set(cleaner.id, `${cleaner.firstname} ${cleaner.lastname}`);
                    }

                    // add this object to unassigned if it is not already assigned
                    if (this.object_assigned_cleaner.has(cleaner.id)) {
                        this.assignedCleaner.add(cleaner.id);
                    } else {
                        this.unassignedCleaner.add(cleaner.id);
                    }
                });
            }
        });

    }

    public async fetchObject(id: number | null): Promise<void> {
        if (!id) {
            return this.fetchNewObject();
        }

        runInAction(() => {
            this.objectDataLoading = true;
        });

        const objectDetails: any = await this.sdk.fetchApi(
            'object',
            'get',
            { object_id: id }
        );

        runInAction(() => {
            this.objectDataLoading = false;
            if (objectDetails?.status === 200) {
                const { object, assigned_cleaner, assigned_products } = objectDetails.json || {};

                if (object) {
                    this.object_id = object.id;
                    this.object_number = object.object_number;
                    this.object_name = object.name;
                    this.object_city = object?.address?.city;
                    this.object_street = object?.address?.street;
                    this.object_note = object?.note || '';
                    this.object_housenumber = object?.address?.housenumber;
                    this.object_zipcode = object?.address?.zipcode;
                    this.selected_manager_id = object.manaer?.id || null; // Set the selected manager
                    this.object_archived = object.archived || false;
                    this.object_representation = object.representation || false;
                    if (object.manager) {
                        this.selected_manager_id = object.manager.id;
                        if (!this.available_managers.find(manager => manager.id === object.manager.id)) {
                            this.available_managers.push({
                                id: object.manager.id,
                                name: `${object.manager.firstname} ${object.manager.lastname}`
                            });
                        }
                    }
                }

                if (assigned_cleaner) {
                    this.object_assigned_cleaner = new Set(assigned_cleaner.map((cleaner: any) => cleaner.id));

                    assigned_cleaner.forEach((cleaner: any) => {
                        if (!this.availableCleaner.has(cleaner.id)) {
                            this.availableCleaner.set(cleaner.id, `${cleaner.firstname} ${cleaner.lastname}`);
                        }

                        if (this.object_assigned_cleaner.has(cleaner.id)) {
                            this.assignedCleaner.add(cleaner.id);
                        } else {
                            this.unassignedCleaner.add(cleaner.id);
                        }
                    });
                }

                if (assigned_products) {
                    this.object_assigned_products = new Set(assigned_products.map((product: any) => product.id));

                    assigned_products.forEach((product: any) => {
                        if (!this.availableProducts.has(product.id)) {
                            this.availableProducts.set(product.id, product);
                        }

                        if (this.object_assigned_products.has(product.id)) {
                            this.assignedProducts.add(product.id);
                        } else {
                            this.unassignedProducts.add(product.id);
                        }
                    });
                }
            }
        });
    }

    public async fetchNewObject(): Promise<void> {
        runInAction(() => {
            this.objectDataLoading = true;
        });

        const objectNewDetails: any = await this.sdk.fetchApi(
            'object',
            'create',
            {}
        );

        runInAction(() => {
            this.objectDataLoading = false;
            if (objectNewDetails?.status === 200) {
                const { assigned_products } = objectNewDetails.json || {};

                if (assigned_products) {
                    this.object_assigned_products = new Set(assigned_products.map((product: any) => product.id));

                    assigned_products.forEach((product: any) => {
                        if (!this.availableProducts.has(product.id)) {
                            this.availableProducts.set(product.id, product);
                        }

                        if (this.object_assigned_products.has(product.id)) {
                            this.assignedProducts.add(product.id);
                        } else {
                            this.unassignedProducts.add(product.id);
                        }
                    });
                }
            }
        });
    }


    get debug() {
        return true;
    }

    public async fetchProducts(): Promise<void> {
        if (this.productDataFetched) {
            return;
        }

        // Reset
        runInAction(() => {
            this.productDataLoading = true;
            this.assignedProducts = new Set();
            this.unassignedProducts = new Set();
        })

        const productList: any = await this.sdk.fetchApi(
            'product',
            'query',
            {
                format: 'simple',
                limit: 999
            }
        );

        runInAction(() => {
            this.productDataLoading = false;

            if (productList?.status === 200) {

                this.productDataFetched = true;

                (productList.json.data as Array<any>).forEach((p: any) => {
                    if (!this.availableProducts.has(p.id)) {
                        this.availableProducts.set(p.id, p);
                    }

                    // add this object to unassigned if it is not already assigned
                    if (this.object_assigned_products.has(p.id)) {
                        this.assignedProducts.add(p.id);
                    } else {
                        this.unassignedProducts.add(p.id);
                    }
                });
            }
        });

    }

    public async onSaveObject(): Promise<void> {
        const data = {
            manager_id: this.selected_manager_id || undefined,
            address: {
                street: this.object_street,
                housenumber: this.object_housenumber,
                zipcode: this.object_zipcode,
                city: this.object_city
            },
            note: this.object_note,
            name: this.object_name,
            object_id: this.object_id || undefined,
            object_number: this.object_number,
            assigned_cleaner: Array.from(this.assignedCleaner),
            assigned_products: Array.from(this.assignedProducts)
        }

        const addObjectRequest: any = await this.sdk.fetchApi(
            'object',
            this.state,
            data
        );

        if (addObjectRequest?.status !== 201 && addObjectRequest?.status !== 200) {
            const errorCode = addObjectRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_VALIDATE_NAME':
                        throw new Error('Bitte gebe einen gültigen Objektnamen an!');
                    case 'ERR_VALIDATE_OBJECT_ID':
                        throw new Error('Bitte gebe eine valide 7-stellige Objekt-Id an!');
                    case 'ERR_VALIDATE_OBJECT_ID_EXIST':
                        throw new Error('Diese Objekt-Id wurde bereits vergeben!');
                    case 'ERR_VALIDATE_OBJECT_NAME_EXIST':
                        throw new Error('Dieser Objekt-Name wurde bereits vergeben!');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public async archiveObject(archived: boolean): Promise<void> {
        const response = await this.sdk.fetchApi('object', 'archive', {
            object_id: this.object_id,
            archived
        });

        if (response?.status !== 200) {
            throw new Error(response?.json?.message || 'Fehler beim Archivieren des Objekts');
        }
    }

    public selectManager(manager_id: number): void {
        this.selected_manager_id = manager_id;
    }

    public updateCleanerAssignment(unassigned: Array<Object_Simple>, assigned: Array<Object_Simple>): void {
        this.assignedCleaner = new Set(assigned.map(obj => obj.id));
        this.unassignedCleaner = new Set(unassigned.map(obj => obj.id));
    }

    public resetProductsAssignment(): void {
        this.assignedProducts = new Set(this.assignedProducts);
        this.unassignedProducts = new Set(this.unassignedProducts);
    }

    public updateProductsAssignment(unassigned: Array<Object_Simple>, assigned: Array<Object_Simple>): void {
        this.assignedProducts = new Set(assigned.map(obj => obj.id));
        this.unassignedProducts = new Set(unassigned.map(obj => obj.id));
    }

    public onChangeTab(tab: number): void {
        if (tab === 1) {
            this.fetchProducts();
        } else if (tab === 0) {
            this.fetchCleaner();
        }
        this.activeTab = tab;
    }

    get assignedCleanerArray(): Array<Object_Simple> {
        return Array.from(this.assignedCleaner).map((id: number) => {
            return {
                id: id,
                name: this.availableCleaner.get(id) || '?'
            }
        });
    }

    get unassignedCleanerArray(): Array<Object_Simple> {
        return Array.from(this.unassignedCleaner).map((id: number) => {
            return {
                id: id,
                name: this.availableCleaner.get(id) || '?'
            }
        });
    }

    get assignedProductsArray(): Array<Object_Simple> {
        return Array.from(this.assignedProducts).map((id: number) => {
            return {
                id: id,
                name: this.availableProducts.get(id)?.name || '?',
                global: this.availableProducts.get(id)?.global || false
            }
        });
    }

    get unassignedProductsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedProducts).map((id: number) => {
            return {
                id: id,
                name: this.availableProducts.get(id)?.name || '?',
                global: this.availableProducts.get(id)?.global || false
            }
        });
    }

    set street(street: string) {
        // limit to 40 characters
        if (street.length <= 80) {
            runInAction(() => {
                this.object_street = street;
            });
        }
    }

    get street(): string {
        return this.object_street;
    }

    set housenumber(housenumber: string) {
        // limit to 40 characters
        if (housenumber.length <= 10) {
            runInAction(() => {
                this.object_housenumber = housenumber;
            });
        }
    }

    get housenumber(): string {
        return this.object_housenumber;
    }

    set zipcode(zipcode: string) {
        // limit to 40 characters
        if (zipcode.length <= 5) {
            runInAction(() => {
                this.object_zipcode = zipcode;
            });
        }
    }

    get zipcode(): string {
        return this.object_zipcode;
    }

    set city(city: string) {
        // limit to 40 characters
        if (city.length <= 80) {
            runInAction(() => {
                this.object_city = city;
            });
        }
    }

    get city(): string {
        return this.object_city;
    }

    set objectname(name: string) {
        // limit to 40 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.object_name = name;
            });
        }
    }

    get objectname(): string {
        return this.object_name;
    }

    set objectid(id: string) {
        // limit to 40 characters
        if (id.length <= 7) {
            runInAction(() => {
                this.object_number = id ? parseInt(id, 10) : null;
            });
        }
    }

    get objectid(): string {
        return `${this.object_number || ''}`;
    }

    set objectnote(txt: string) {
        if (txt.length <= 255) {
            runInAction(() => {
                this.object_note = txt;
            });
        }
    }

    get objectnote(): string {
        return this.object_note;
    }

    get archived(): boolean {
        return this.object_archived;
    }

    get saveButton(): string {
        return this.state === 'update' ? 'Aktualisieren' : 'Erstellen';
    }

    get isCloneable(): boolean {
        return this.state !== 'add' && !this.isRepresentation && !this.objectDataLoading;
    }

    get isArchivable(): boolean {
        return this.state !== 'add' && !this.isRepresentation && !this.objectDataLoading;
    }

    get header(): string {
        return this.state === 'update' ? 'Objekt bearbeiten' : 'Neues Objekt anlegen';
    }

    get id(): string {
        return 'object';
    }

    get isRepresentation(): boolean {
        return this.object_representation;
    }

    get isLoading(): boolean {
        return this.objectDataLoading;
    }

    get isInvalid(): boolean {
        return !(this.validObjectname && this.validObjectId)
    }

    get validObjectname(): boolean {
        return this.object_name.length >= 3;
    }

    get validObjectId(): boolean {
        return typeof this.object_number === 'number' && `${this.object_number}`.length === 7;
    }
}

export default ObjectDialogStore;